import { theme, ThemeConfig } from 'antd';
import {
  COLOR_TOKENS,
  FONT_FAMILY,
  FONT_SIZE_TOKENS,
  MOTION,
  RADIUS_TOKENS,
  SHADOW_TOKENS,
} from 'styles/tokens';
import { ANTD_CSS_VAR_KEY, ResolvedTheme, THEME_MODE } from './constants';

// The image preview's close button, arrows, toolbar and thumbnail hover cover sit on antd's black mask
// in both themes, and antd paints them in `colorTextLightSolid`. Here that token is `onPrimary`, which
// is near-black in dark, so the close X vanished. antd derives the toolbar shades from this token too.
const IMAGE_ON_MASK_COLOR = '#ffffff';

const TABLE_HEADER_PADDING_BLOCK = 10;

const seconds = (ms: number) => `${ms / 1000}s`;

// Modals, drawers, dropdowns, selects and tooltips all animate from these, so antd moves in the same
// band and on the same curves as the rest of the app. No overshoot ("back") curves: nothing bounces.
const MOTION_TOKENS = {
  motionDurationFast: seconds(MOTION.duration.fast),
  motionDurationMid: seconds(MOTION.duration.base),
  motionDurationSlow: seconds(MOTION.duration.slow),
  motionEaseOut: MOTION.easing.out,
  motionEaseOutCirc: MOTION.easing.out,
  motionEaseOutQuint: MOTION.easing.out,
  motionEaseOutBack: MOTION.easing.out,
  motionEaseInQuint: MOTION.easing.in,
  motionEaseInBack: MOTION.easing.in,
  motionEaseInOut: MOTION.easing.inOut,
  motionEaseInOutCirc: MOTION.easing.inOut,
};

interface IAntdThemeOptions {
  // The OS asks for reduced motion: antd then skips its enter/leave animations altogether.
  isReducedMotion: boolean;
}

// antd derives hover/active/background shades from the seed colours with real colour maths, so it
// gets the token values themselves and not the CSS variables the rest of the app uses.
export const buildAntdTheme = (
  resolvedTheme: ResolvedTheme,
  { isReducedMotion }: IAntdThemeOptions,
): ThemeConfig => {
  const colors = COLOR_TOKENS[resolvedTheme];
  const isDark = resolvedTheme === THEME_MODE.DARK;
  const shadow = SHADOW_TOKENS[resolvedTheme];

  return {
    // Without the per-theme hash class the app's `[class$='ant-…']` selectors keep matching.
    hashed: false,
    // antd 6 always appends its CSS-variable scope class (plus `ant-<component>-css-var`) to a
    // component root. A fixed key lets the stylesheets match "ends with X, ignoring that class":
    // `:is([class$='X'], :where([class$='X css-var-timego'], [class*='X css-var-timego '][class$='-css-var']))`
    // (the `:where` keeps the specificity of the original `[class$='X']`).
    cssVar: { key: ANTD_CSS_VAR_KEY },
    algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: colors.primary,
      colorPrimaryHover: colors.primaryHover,
      colorPrimaryActive: colors.primaryActive,
      colorInfo: colors.primary,
      // Links are body-size text: `primaryText`, not the filled `primary` (4.3:1 on the page in light).
      colorLink: colors.primaryText,
      colorLinkHover: colors.primaryHover,
      colorLinkActive: colors.primaryActive,
      colorSuccess: colors.success,
      colorWarning: colors.warning,
      colorError: colors.danger,
      colorTextBase: colors.text,
      colorText: colors.text,
      colorTextSecondary: colors.textMuted,
      // antd derives this one (Empty captions, form extras) as 45% alpha of the text colour: 2.4:1 in light.
      colorTextDescription: colors.textMuted,
      colorTextPlaceholder: colors.textPlaceholder,
      colorTextDisabled: colors.textDisabled,
      colorTextLightSolid: colors.onPrimary,
      colorBgBase: colors.surface,
      colorBgLayout: colors.background,
      colorBgContainer: colors.surface,
      colorBgElevated: isDark ? colors.surfaceMuted : colors.surface,
      colorBorder: colors.border,
      colorBorderSecondary: colors.border,
      borderRadiusXS: 4,
      borderRadiusSM: RADIUS_TOKENS.sm,
      borderRadius: RADIUS_TOKENS.md,
      borderRadiusLG: RADIUS_TOKENS.lg,
      fontFamily: FONT_FAMILY,
      fontSize: FONT_SIZE_TOKENS.base,
      // No text below 14px (Ali, 2026-10-09: many older users). antd derives 12px for tags, badges and
      // small controls from this token.
      fontSizeSM: FONT_SIZE_TOKENS.base,
      boxShadow: shadow,
      boxShadowSecondary: shadow,
      boxShadowTertiary: shadow,
      ...MOTION_TOKENS,
      motion: !isReducedMotion,
    },
    components: {
      // antd's 2px drop shadow under buttons read as a lighter stripe under every primary button in dark
      Button: { primaryShadow: 'none', defaultShadow: 'none', dangerShadow: 'none' },
      Layout: { headerBg: colors.surface, bodyBg: colors.background },
      // Dropdown rows sit 4px inside a popup rounded at lg; md keeps the two corners concentric.
      Menu: { itemBorderRadius: RADIUS_TOKENS.md, subMenuItemBorderRadius: RADIUS_TOKENS.md },
      // antd keeps 1px before a label's colon; at the 12px filter labels it reads as "Jobs :".
      Form: { labelColonMarginInlineStart: 0 },
      Image: { colorTextLightSolid: IMAGE_ON_MASK_COLOR },
      // antd 6 made the modal header transparent; the full-page modals paint their body in the page
      // colour and relied on antd 5's elevated header band.
      Modal: { headerBg: isDark ? colors.surfaceMuted : colors.surface },
      // The unfilled star defaults to a 6 to 18% fill, which nearly vanishes on the page in both themes.
      Rate: { starBg: colors.textPlaceholder },
      Table: {
        // Header cells 10px top and bottom (antd's 16 made a two-line header ~80px tall); body cells take
        // $table-cell-padding from the stylesheet.
        cellPaddingBlock: TABLE_HEADER_PADDING_BLOCK,
        headerBg: colors.surfaceMuted,
        headerColor: colors.textMuted,
        borderColor: colors.border,
      },
    },
  };
};
